import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import {
  getStockEngines, createStockEngine, updateStockEngine, deleteStockEngine,
  StockEngine, StockPolicy, StockRisk, StockStrategy, StockSchedule, UniversePreset,
  CreateStockEngineRequest,
} from '../../services/ttStockTraderService';
import '../Dashboard.css';
import { RiskDisclaimer } from '../shared/RiskDisclaimer';

const cardStyle: React.CSSProperties = { background: '#1f2937', border: '1px solid #374151', borderRadius: 10, padding: '20px 24px', marginBottom: 12 };
const labelStyle: React.CSSProperties = { fontSize: 13, color: '#9ca3af', marginBottom: 4, display: 'block' };
const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: 6, background: '#111827', color: '#f9fafb', border: '1px solid #374151', fontSize: 14 };
const selectStyle: React.CSSProperties = { ...inputStyle, cursor: 'pointer' };
const btnPrimary: React.CSSProperties = { padding: '10px 24px', background: '#6d28d9', color: '#f9fafb', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 };
const btnSecondary: React.CSSProperties = { ...btnPrimary, background: '#374151' };
const btnDanger: React.CSSProperties = { ...btnPrimary, background: '#991b1b', fontSize: 12, padding: '6px 12px' };

const STRATEGIES: { value: StockStrategy; label: string; group: 'bullish' | 'bearish' | 'auto' }[] = [
  { value: 'stock_signals', label: 'Stock Research (mirror prod-stock-signals)', group: 'auto' },
  { value: 'auto-directional', label: 'Auto-directional (pick best of all)', group: 'auto' },
  { value: 'momentum', label: 'Momentum (bullish)', group: 'bullish' },
  { value: 'breakout', label: 'Breakout (bullish)', group: 'bullish' },
  { value: 'oversold-bounce', label: 'Oversold bounce (bullish mean-revert)', group: 'bullish' },
  { value: 'trend-following', label: 'Trend-following (bullish)', group: 'bullish' },
  { value: 'bearish-momentum', label: 'Bearish momentum', group: 'bearish' },
  { value: 'breakdown', label: 'Breakdown (bearish)', group: 'bearish' },
  { value: 'overbought-reversal', label: 'Overbought reversal (bearish mean-revert)', group: 'bearish' },
  { value: 'bearish-trend-following', label: 'Bearish trend-following', group: 'bearish' },
];

const RISK_OPTIONS: { value: StockRisk; label: string; short: string }[] = [
  { value: 'conservative', label: 'Conservative', short: 'Tighter stops, smaller size, fewer concurrent positions' },
  { value: 'moderate', label: 'Moderate', short: 'Standard ATR-based geometry' },
  { value: 'aggressive', label: 'Aggressive', short: 'Wider stops, more concurrent positions' },
];

const SCHEDULE_OPTIONS: { value: StockSchedule; label: string }[] = [
  { value: 'daily', label: 'Once daily at 9:15am ET' },
  { value: '60min', label: 'Every hour (market hours)' },
  { value: '30min', label: 'Every 30 minutes' },
  { value: '15min', label: 'Every 15 minutes' },
  { value: '60min_ext', label: 'Every hour (extended: 4am-7pm ET)' },
  { value: '30min_ext', label: 'Every 30 minutes (extended)' },
  { value: '15min_ext', label: 'Every 15 minutes (extended)' },
];

const PRESET_OPTIONS: { value: UniversePreset; label: string; sample: string[] }[] = [
  { value: 'broad_etfs', label: 'Broad ETFs', sample: ['SPY', 'QQQ', 'IWM', 'DIA'] },
  { value: 'spdr_sectors', label: 'SPDR sectors (11 ETFs)', sample: ['XLK', 'XLE', 'XLF', 'XLY', 'XLP', 'XLV', 'XLI', 'XLB', 'XLU', 'XLRE', 'XLC'] },
  { value: 'mega_cap_tech', label: 'Mega-cap tech', sample: ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'META', 'NVDA', 'TSLA', 'AVGO'] },
  { value: 'custom', label: 'Custom (paste symbols)', sample: [] },
];

// Universe options for the stock_signals strategy — empty universe means
// "mirror every open signal", filter restricts to a subset.
const SIGNALS_PRESET_OPTIONS: { value: UniversePreset; label: string; sample: string[] }[] = [
  { value: 'all_signals', label: 'All picks (no filter)', sample: [] },
  { value: 'custom', label: 'Custom (restrict to these symbols)', sample: [] },
];

interface PolicyDraft {
  policyId: string;
  strategyId: StockStrategy;
  riskAppetite: StockRisk;
  universePreset: UniversePreset;
  symbolsText: string;
  maxPerRun: number;
  // 0 = inherit risk-profile default; >0 = explicit override (1-50)
  maxOpenTradesOverride: number;
}

interface FormState {
  name: string;
  schedule: StockSchedule;
  paperMode: boolean;
  regimeOverlay: boolean;
  extendedHours: boolean;
  startingEquity: number;
  policies: PolicyDraft[];
  disclaimerAccepted: boolean;
  selectedConnectionId: string;
  // Stepper / patient-mode knobs — see 2026-06-03-tt-stock-fill-quality
  stepSizePct: number;       // 0.0005 .. 0.02
  maxDeviationPct: number;   // 0.001 .. 0.05
  maxAgeHours: number;       // 1 .. 72
  preferLowerClose: boolean;
}

const defaultPolicy = (): PolicyDraft => ({
  policyId: '',
  strategyId: 'auto-directional',
  riskAppetite: 'moderate',
  universePreset: 'broad_etfs',
  symbolsText: '',
  maxPerRun: 1,
  maxOpenTradesOverride: 0,
});

const defaultForm: FormState = {
  name: '',
  schedule: 'daily',
  paperMode: true,
  regimeOverlay: true,
  extendedHours: false,
  startingEquity: 100000,
  policies: [defaultPolicy()],
  disclaimerAccepted: false,
  selectedConnectionId: 'legacy',
  stepSizePct: 0.0025,
  maxDeviationPct: 0.01,
  maxAgeHours: 8,
  preferLowerClose: false,
};

function policyToDraft(p: StockPolicy): PolicyDraft {
  // A stock_signals policy with empty symbols means "mirror all" — surface
  // that as the all_signals preset in the form (not custom-with-no-symbols).
  let preset: UniversePreset;
  if (p.universePreset) {
    preset = p.universePreset as UniversePreset;
  } else if (p.strategyId === 'stock_signals' && (!p.symbols || p.symbols.length === 0)) {
    preset = 'all_signals';
  } else {
    preset = 'custom';
  }
  return {
    policyId: p.policyId,
    strategyId: p.strategyId,
    riskAppetite: p.riskAppetite,
    universePreset: preset,
    symbolsText: (p.symbols || []).join(', '),
    maxPerRun: p.maxPerRun != null ? p.maxPerRun : (p.strategyId === 'stock_signals' ? 3 : 1),
    maxOpenTradesOverride: p.maxOpenTradesOverride && p.maxOpenTradesOverride > 0 ? p.maxOpenTradesOverride : 0,
  };
}

function draftToPolicy(d: PolicyDraft): NonNullable<CreateStockEngineRequest['policies']>[number] {
  const base: any = {
    policyId: d.policyId || undefined,
    strategyId: d.strategyId,
    riskAppetite: d.riskAppetite,
    maxPerRun: Math.max(1, Math.min(10, Math.round(d.maxPerRun || 1))),
  };
  // Override is optional. 0 / blank means inherit from risk profile.
  if (d.maxOpenTradesOverride && d.maxOpenTradesOverride > 0) {
    base.maxOpenTradesOverride = Math.max(1, Math.min(50, Math.round(d.maxOpenTradesOverride)));
  }
  if (d.universePreset === 'all_signals') {
    // No filter — backend treats empty symbols + strategy=stock_signals
    // as "mirror every open signal".
    base.symbols = [];
  } else if (d.universePreset === 'custom') {
    base.symbols = d.symbolsText.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
  } else {
    base.universePreset = d.universePreset;
  }
  return base;
}

const TtStockEngines: React.FC = () => {
  const { user, token } = useAuth();
  const [engines, setEngines] = useState<StockEngine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>({ ...defaultForm });

  const u = user as any;
  const ttConnections: any[] = u?.tastyTradeConnections || [];
  const legacyTtAccount = u?.tastyTradeAccountNumber || '';
  const allConnections: Array<{ id: string; label: string; account: string }> = [
    ...(legacyTtAccount && u?.tastyTradeConnected ? [{ id: 'legacy', label: `Main (${legacyTtAccount})`, account: legacyTtAccount }] : []),
    ...ttConnections.map((c: any) => ({ id: c.connectionId, label: `${c.label || 'Account'} (${c.accountNumber})`, account: c.accountNumber })),
  ];

  const load = async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError('');
      const data = await getStockEngines(token);
      setEngines(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load engines');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const resetForm = () => {
    setForm({ ...defaultForm });
    setShowCreate(false);
    setEditingId(null);
  };

  const beginEdit = (e: StockEngine) => {
    setEditingId(e.engineId);
    setShowCreate(true);
    setForm({
      name: e.name,
      schedule: e.schedule,
      paperMode: e.paperMode,
      regimeOverlay: e.stockConfig?.regimeOverlay !== false,
      extendedHours: e.stockConfig?.extendedHours === true,
      startingEquity: e.stockConfig?.startingEquity || 100000,
      policies: (e.stockConfig?.policies || []).map(policyToDraft),
      disclaimerAccepted: false,
      selectedConnectionId: e.tastytradeConnectionId || 'legacy',
      stepSizePct: e.stockFillConfig?.stepSizePct ?? 0.0025,
      maxDeviationPct: e.stockFillConfig?.maxDeviationPct ?? 0.01,
      maxAgeHours: e.stockFillConfig?.maxAgeHours ?? 8,
      preferLowerClose: e.stockFillConfig?.preferLowerClose === true,
    });
  };

  const handleSubmit = async () => {
    if (!token) return;
    if (!form.name.trim()) { setError('Engine name is required'); return; }
    if (form.policies.length === 0) { setError('Add at least one policy'); return; }
    if (!form.paperMode && !form.disclaimerAccepted && !editingId) {
      setError('Live mode requires accepting the disclaimer'); return;
    }
    if (!form.paperMode && allConnections.length === 0) {
      setError('Live mode requires a connected TastyTrade account — connect one in Profile first'); return;
    }

    const conn = allConnections.find((c) => c.id === form.selectedConnectionId);
    const body: CreateStockEngineRequest = {
      name: form.name.trim(),
      schedule: form.schedule,
      paperMode: form.paperMode,
      regimeOverlay: form.regimeOverlay,
      extendedHours: form.extendedHours,
      startingEquity: form.startingEquity,
      policies: form.policies.map(draftToPolicy),
      stockFillConfig: {
        stepSizePct: form.stepSizePct,
        maxDeviationPct: form.maxDeviationPct,
        maxAgeHours: form.maxAgeHours,
        preferLowerClose: form.preferLowerClose,
      },
      ...(conn ? { connectionId: conn.id, accountNumber: conn.account } : {}),
      ...(form.disclaimerAccepted ? { disclaimerAccepted: true } : {}),
    };

    try {
      setSaving(true);
      setError('');
      if (editingId) await updateStockEngine(token, editingId, body);
      else await createStockEngine(token, body);
      resetForm();
      await load();
    } catch (e: any) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (engineId: string, name: string) => {
    if (!token) return;
    if (!window.confirm(`Delete engine "${name}"? This removes the EventBridge cron + DDB record. Existing trades stay.`)) return;
    try { await deleteStockEngine(token, engineId); await load(); }
    catch (e: any) { setError(e?.message || 'Delete failed'); }
  };

  const handleToggleStatus = async (e: StockEngine) => {
    if (!token) return;
    const next = e.status === 'active' ? 'paused' : 'active';
    try { await updateStockEngine(token, e.engineId, { status: next }); await load(); }
    catch (err: any) { setError(err?.message || 'Toggle failed'); }
  };

  const handleTogglePaper = async (e: StockEngine) => {
    if (!token) return;
    if (e.paperMode) {
      // Going LIVE — require confirmation
      if (!window.confirm(`Switch "${e.name}" to LIVE trading? Real orders will be placed on your TastyTrade account.`)) return;
      try { await updateStockEngine(token, e.engineId, { paperMode: false, disclaimerAccepted: true }); await load(); }
      catch (err: any) { setError(err?.message || 'Toggle failed'); }
    } else {
      // Going PAPER — no extra friction
      try { await updateStockEngine(token, e.engineId, { paperMode: true }); await load(); }
      catch (err: any) { setError(err?.message || 'Toggle failed'); }
    }
  };

  return (
    <div className="dashboard-container">
      <Navigation />
      <div className="dashboard-content" style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 24px', color: '#f9fafb' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>TT Stocks — Engines</h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#9ca3af' }}>
              TastyTrade equity auto-traders. Each engine runs one or more policies on a cron and places stock bracket orders (paper or live).
            </p>
          </div>
          {!showCreate && (
            <button style={btnPrimary} onClick={() => { setShowCreate(true); setEditingId(null); setForm({ ...defaultForm }); }}>+ New engine</button>
          )}
        </div>

        {error && (
          <div style={{ background: '#7f1d1d40', border: '1px solid #991b1b', color: '#fca5a5', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{error}</div>
        )}

        {showCreate && (
          <div style={cardStyle}>
            <h2 style={{ margin: '0 0 14px', fontSize: '1.1rem' }}>{editingId ? 'Edit engine' : 'New engine'}</h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Engine name</label>
                <input style={inputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Sector ETF momentum" />
              </div>
              <div>
                <label style={labelStyle}>Schedule</label>
                <select style={selectStyle} value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value as StockSchedule })}>
                  {SCHEDULE_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Mode</label>
                <select style={selectStyle} value={form.paperMode ? 'paper' : 'live'} onChange={(e) => setForm({ ...form, paperMode: e.target.value === 'paper' })}>
                  <option value="paper">Paper (simulated)</option>
                  <option value="live">Live (real money)</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Regime overlay</label>
                <select style={selectStyle} value={form.regimeOverlay ? 'on' : 'off'} onChange={(e) => setForm({ ...form, regimeOverlay: e.target.value === 'on' })}>
                  <option value="on">On (±10% score)</option>
                  <option value="off">Off (raw scores)</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Extended hours</label>
                <select style={selectStyle} value={form.extendedHours ? 'on' : 'off'} onChange={(e) => setForm({ ...form, extendedHours: e.target.value === 'on' })}>
                  <option value="off">Off (RTH only, 9:30am-4pm ET)</option>
                  <option value="on">On (pre/after-hours, 4am-8pm ET)</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Sizing equity ($) {form.paperMode ? '' : '— ignored in live mode'}</label>
                {form.paperMode ? (
                  <input style={inputStyle} type="number" value={form.startingEquity} onChange={(e) => setForm({ ...form, startingEquity: parseFloat(e.target.value) || 0 })} />
                ) : (
                  <div style={{ ...inputStyle, color: '#9ca3af', display: 'flex', alignItems: 'center', minHeight: 36, fontStyle: 'italic' }}>
                    Auto: TT account equityBP
                  </div>
                )}
              </div>
            </div>
            {!form.paperMode && (
              <div style={{ marginTop: -8, marginBottom: 14, fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
                Live engines size off the real TT account's equityBP (read from tt-account-snapshots, ≤60min old; refreshed live from TT if stale). The Dashboard's "Live broker account" panel shows the current value.
              </div>
            )}

            {!form.paperMode && allConnections.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>TastyTrade account</label>
                <select style={selectStyle} value={form.selectedConnectionId} onChange={(e) => setForm({ ...form, selectedConnectionId: e.target.value })}>
                  {allConnections.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
            )}

            {/* Fill behavior — patient stepper knobs */}
            <details style={{ marginTop: 8, marginBottom: 14, background: '#111827', border: '1px solid #374151', borderRadius: 8, padding: 12 }}>
              <summary style={{ ...labelStyle, marginBottom: 0, cursor: 'pointer', userSelect: 'none' }}>Fill behavior (patient stepper)</summary>
              <div style={{ marginTop: 12, fontSize: 12, color: '#9ca3af', marginBottom: 10 }}>
                Entry uses live quote mid (not prior close). If unfilled, the position monitor walks the limit toward the ask on each 15-min tick. When the cap is hit, the OTOCO chain is cancelled and a fresh pick is chosen next analyzer run.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Step size (%)</label>
                  <input
                    style={inputStyle}
                    type="number"
                    step="0.05"
                    min="0.05"
                    max="2"
                    value={+(form.stepSizePct * 100).toFixed(2)}
                    onChange={(e) => setForm({ ...form, stepSizePct: (parseFloat(e.target.value) || 0) / 100 })}
                  />
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>per-tick walk size</div>
                </div>
                <div>
                  <label style={labelStyle}>Max deviation (%)</label>
                  <input
                    style={inputStyle}
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="5"
                    value={+(form.maxDeviationPct * 100).toFixed(2)}
                    onChange={(e) => setForm({ ...form, maxDeviationPct: (parseFloat(e.target.value) || 0) / 100 })}
                  />
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>cap above pick entry</div>
                </div>
                <div>
                  <label style={labelStyle}>Max age (hours)</label>
                  <input
                    style={inputStyle}
                    type="number"
                    step="1"
                    min="1"
                    max="72"
                    value={form.maxAgeHours}
                    onChange={(e) => setForm({ ...form, maxAgeHours: parseFloat(e.target.value) || 8 })}
                  />
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>hard-cancel after</div>
                </div>
                <div>
                  <label style={labelStyle}>Anchor</label>
                  <select
                    style={selectStyle}
                    value={form.preferLowerClose ? 'min' : 'mid'}
                    onChange={(e) => setForm({ ...form, preferLowerClose: e.target.value === 'min' })}
                  >
                    <option value="mid">Quote mid</option>
                    <option value="min">min(mid, prior close)</option>
                  </select>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>entry price source</div>
                </div>
              </div>
            </details>

            {/* Policies */}
            <div style={{ marginTop: 8, marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <label style={labelStyle}>Policies (strategy × universe × risk)</label>
                <button style={{ ...btnSecondary, padding: '4px 10px', fontSize: 12 }} onClick={() => setForm({ ...form, policies: [...form.policies, defaultPolicy()] })}>+ Add policy</button>
              </div>
              {form.policies.map((p, idx) => {
                const presetList = p.strategyId === 'stock_signals' ? SIGNALS_PRESET_OPTIONS : PRESET_OPTIONS;
                const preset = presetList.find((x) => x.value === p.universePreset);
                return (
                  <div key={idx} style={{ background: '#111827', border: '1px solid #374151', borderRadius: 8, padding: 12, marginBottom: 8 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 10, alignItems: 'end' }}>
                      <div>
                        <label style={labelStyle}>Strategy</label>
                        <select style={selectStyle} value={p.strategyId} onChange={(e) => {
                          const nextStrategy = e.target.value as StockStrategy;
                          const next = [...form.policies];
                          // When switching to/from stock_signals, reset the
                          // universe preset to the sensible default for that
                          // strategy so users don't end up with a 7-ETF
                          // filter applied to all signals by accident.
                          const newUniverse: UniversePreset = nextStrategy === 'stock_signals'
                            ? 'all_signals'
                            : (p.universePreset === 'all_signals' ? 'broad_etfs' : p.universePreset);
                          next[idx] = { ...p, strategyId: nextStrategy, universePreset: newUniverse };
                          setForm({ ...form, policies: next });
                        }}>
                          {STRATEGIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={labelStyle}>Risk</label>
                        <select style={selectStyle} value={p.riskAppetite} onChange={(e) => {
                          const next = [...form.policies]; next[idx] = { ...p, riskAppetite: e.target.value as StockRisk }; setForm({ ...form, policies: next });
                        }}>
                          {RISK_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={labelStyle}>Universe</label>
                        <select style={selectStyle} value={p.universePreset} onChange={(e) => {
                          const next = [...form.policies]; next[idx] = { ...p, universePreset: e.target.value as UniversePreset }; setForm({ ...form, policies: next });
                        }}>
                          {(p.strategyId === 'stock_signals' ? SIGNALS_PRESET_OPTIONS : PRESET_OPTIONS)
                            .map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                      <button style={btnDanger} onClick={() => {
                        const next = form.policies.filter((_, i) => i !== idx); setForm({ ...form, policies: next.length ? next : [defaultPolicy()] });
                      }}>Remove</button>
                    </div>
                    <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '160px 220px 1fr', gap: 12, alignItems: 'center' }}>
                      <div>
                        <label style={labelStyle}>Max trades per run</label>
                        <input
                          style={inputStyle}
                          type="number"
                          min={1}
                          max={10}
                          step={1}
                          value={p.maxPerRun}
                          onChange={(e) => {
                            const v = Math.max(1, Math.min(10, parseInt(e.target.value, 10) || 1));
                            const next = [...form.policies]; next[idx] = { ...p, maxPerRun: v }; setForm({ ...form, policies: next });
                          }}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Open-trade cap override</label>
                        <input
                          style={inputStyle}
                          type="number"
                          min={0}
                          max={50}
                          step={1}
                          value={p.maxOpenTradesOverride}
                          placeholder="inherit"
                          onChange={(e) => {
                            const v = Math.max(0, Math.min(50, parseInt(e.target.value, 10) || 0));
                            const next = [...form.policies]; next[idx] = { ...p, maxOpenTradesOverride: v }; setForm({ ...form, policies: next });
                          }}
                        />
                      </div>
                      <div style={{ fontSize: 11, color: '#6b7280', alignSelf: 'end', marginBottom: 8 }}>
                        Max per run = top N candidates by score per cron tick. Override = explicit cap for this policy (0 inherits risk-profile default: conservative=3, moderate=7, aggressive=15).
                      </div>
                    </div>
                    {p.universePreset === 'custom' ? (
                      <div style={{ marginTop: 10 }}>
                        <label style={labelStyle}>Symbols (comma- or space-separated)</label>
                        <input style={inputStyle} value={p.symbolsText} placeholder="AAPL, MSFT, NVDA, ..." onChange={(e) => {
                          const next = [...form.policies]; next[idx] = { ...p, symbolsText: e.target.value }; setForm({ ...form, policies: next });
                        }} />
                      </div>
                    ) : p.universePreset === 'all_signals' ? (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
                        Mirrors every open pick in <strong>prod-stock-signals</strong> with no symbol filter.
                      </div>
                    ) : preset && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#9ca3af' }}>
                        <span style={{ color: '#6b7280' }}>{preset.sample.length} symbols:</span> {preset.sample.slice(0, 10).join(', ')}{preset.sample.length > 10 ? '…' : ''}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {!form.paperMode && !editingId && (
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 14, fontSize: 13, color: '#fca5a5' }}>
                <input type="checkbox" checked={form.disclaimerAccepted} onChange={(e) => setForm({ ...form, disclaimerAccepted: e.target.checked })} style={{ marginTop: 3 }} />
                <span>I understand this engine will place real equity orders on my connected TastyTrade account. I accept responsibility for any losses. Past performance does not guarantee future results.</span>
              </label>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button style={btnPrimary} disabled={saving} onClick={handleSubmit}>{saving ? 'Saving…' : (editingId ? 'Save changes' : 'Create engine')}</button>
              <button style={btnSecondary} onClick={resetForm}>Cancel</button>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ color: '#9ca3af', padding: 20 }}>Loading…</div>
        ) : engines.length === 0 ? (
          <div style={{ ...cardStyle, color: '#9ca3af', textAlign: 'center', padding: 32 }}>
            No TT stock engines yet. Click "+ New engine" to get started.
          </div>
        ) : (
          engines.map((e) => (
            <div key={e.engineId} style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <h3 style={{ margin: 0, fontSize: '1.15rem' }}>{e.name}</h3>
                    <span style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
                      padding: '2px 8px', borderRadius: 3,
                      background: e.paperMode ? '#d9770640' : '#16a34a40',
                      color: e.paperMode ? '#fbbf24' : '#86efac',
                    }}>{e.paperMode ? 'PAPER' : 'LIVE'}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
                      padding: '2px 8px', borderRadius: 3,
                      background: e.status === 'active' ? '#16a34a40' : '#37415140',
                      color: e.status === 'active' ? '#86efac' : '#9ca3af',
                    }}>{e.status.toUpperCase()}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                    {e.stockConfig?.policies?.length || 0} polic{(e.stockConfig?.policies?.length || 0) === 1 ? 'y' : 'ies'} · schedule {e.schedule} · regime {e.stockConfig?.regimeOverlay !== false ? 'on' : 'off'}
                    {e.tastytradeAccountNumber ? ` · acct ${e.tastytradeAccountNumber}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button style={{ ...btnSecondary, fontSize: 12, padding: '6px 12px' }} onClick={() => handleTogglePaper(e)}>
                    {e.paperMode ? 'Go live →' : '← To paper'}
                  </button>
                  <button style={{ ...btnSecondary, fontSize: 12, padding: '6px 12px' }} onClick={() => handleToggleStatus(e)}>
                    {e.status === 'active' ? 'Pause' : 'Resume'}
                  </button>
                  <button style={{ ...btnSecondary, fontSize: 12, padding: '6px 12px' }} onClick={() => beginEdit(e)}>Edit</button>
                  <button style={btnDanger} onClick={() => handleDelete(e.engineId, e.name)}>Delete</button>
                </div>
              </div>
              {e.stockConfig?.policies?.map((p) => (
                <div key={p.policyId} style={{ background: '#111827', border: '1px solid #374151', borderRadius: 6, padding: '8px 12px', marginTop: 6, fontSize: 12 }}>
                  <div style={{ color: '#f9fafb', fontWeight: 600 }}>{p.strategyId} · {p.riskAppetite}</div>
                  <div style={{ color: '#9ca3af', marginTop: 2 }}>
                    {p.strategyId === 'stock_signals' && (!p.symbols || p.symbols.length === 0)
                      ? 'All open picks (no filter)'
                      : `${p.symbols.length} symbols: ${p.symbols.slice(0, 12).join(', ')}${p.symbols.length > 12 ? `, +${p.symbols.length - 12} more` : ''}`}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
      <RiskDisclaimer />
    </div>
  );
};

export default TtStockEngines;
